import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Users, DollarSign, Building2, TrendingUp, Download, Calendar,
  Copy, CheckCircle2, Clock, LogOut, User, Lock, ChevronRight,
  ArrowUpRight, ArrowDownRight, Minus, Eye, EyeOff, BarChart3,
  FileText, Settings
} from 'lucide-react';

// API helper for referrer portal
const referrerApi = {
  baseUrl: process.env.REACT_APP_BACKEND_URL || '',
  
  getToken() {
    return localStorage.getItem('referrer_token');
  },
  
  setToken(token) {
    localStorage.setItem('referrer_token', token);
  },
  
  clearToken() {
    localStorage.removeItem('referrer_token');
    localStorage.removeItem('referrer_data');
  },
  
  async request(endpoint, options = {}) {
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };
    
    const response = await fetch(`${this.baseUrl}/api/referrals${endpoint}`, {
      ...options,
      headers
    });
    
    if (response.status === 401) {
      this.clearToken();
      window.location.href = '/referrer-portal';
      throw new Error('Session expired');
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    
    // Handle CSV download
    if (response.headers.get('content-type')?.includes('text/csv')) {
      return response.blob();
    }
    
    return response.json();
  },
  
  login: (email, password) => referrerApi.request('/portal/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  }),
  
  getDashboard: () => referrerApi.request('/portal/dashboard'),
  getCommissions: (params) => referrerApi.request(`/portal/commissions?${new URLSearchParams(params)}`),
  getCompanies: () => referrerApi.request('/portal/companies'),
  getProfile: () => referrerApi.request('/portal/me'),
  updateProfile: (data) => referrerApi.request('/portal/profile', {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  changePassword: (data) => referrerApi.request('/portal/change-password', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  exportCommissions: () => referrerApi.request('/portal/commissions/export')
};

// Login Component
function ReferrerLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await referrerApi.login(email, password);
      referrerApi.setToken(response.access_token);
      localStorage.setItem('referrer_data', JSON.stringify(response.referrer));
      toast.success(`Welcome back, ${response.referrer.full_name}!`);
      onLogin(response.referrer);
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white/95 backdrop-blur shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Referrer Portal
          </h1>
          <p className="text-gray-500 mt-2">Sign in to view your commissions</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-gray-700">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5"
              required
              data-testid="referrer-email-input"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-700">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="referrer-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="referrer-login-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Need access? Contact your administrator.
        </p>
      </Card>
    </div>
  );
}

// Dashboard Component
function ReferrerDashboard({ referrer, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await referrerApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  }, []);

  const fetchCommissions = useCallback(async () => {
    try {
      const data = await referrerApi.getCommissions({});
      setCommissions(data.commissions || []);
    } catch (error) {
      toast.error('Failed to load commissions');
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await referrerApi.getCompanies();
      setCompanies(data.companies || []);
    } catch (error) {
      toast.error('Failed to load companies');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchDashboard();
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'commissions') fetchCommissions();
    if (activeTab === 'companies') fetchCompanies();
  }, [activeTab, fetchCommissions, fetchCompanies]);

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Referral code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleExport = async () => {
    try {
      const blob = await referrerApi.exportCommissions();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissions_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-purple-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Partner Portal
                </h1>
                <p className="text-xs text-gray-500">Integrity Home Care</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{referrer.full_name}</p>
                <p className="text-xs text-gray-500">{referrer.commission_rate}% commission rate</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="gap-2"
                data-testid="referrer-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-200 p-1 rounded-xl mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-900 rounded-lg gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="companies" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-900 rounded-lg gap-2">
              <Building2 className="w-4 h-4" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="commissions" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-900 rounded-lg gap-2">
              <DollarSign className="w-4 h-4" />
              Commissions
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-900 rounded-lg gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <Card className="p-5 bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Total Earned</p>
                    <p className="text-3xl font-bold mt-1">{formatCurrency(dashboard?.summary?.total_earned)}</p>
                  </div>
                  <div className="p-3 bg-white/20 rounded-xl">
                    <DollarSign className="w-6 h-6" />
                  </div>
                </div>
              </Card>

              <Card className="p-5 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">
                      {formatCurrency(dashboard?.summary?.total_pending)}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-5 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Paid</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {formatCurrency(dashboard?.summary?.total_paid)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-xl">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-5 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">Referrals</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {dashboard?.summary?.total_referrals || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Building2 className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Referral Codes */}
            <Card className="p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Referral Codes</h3>
              {dashboard?.referral_codes?.length > 0 ? (
                <div className="grid gap-3">
                  {dashboard.referral_codes.map((code) => (
                    <div
                      key={code.code}
                      className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100"
                    >
                      <div className="flex items-center gap-4">
                        <code className="text-lg font-mono font-bold text-purple-700 bg-white px-4 py-2 rounded-lg border border-purple-200">
                          {code.code}
                        </code>
                        <div>
                          <p className="text-sm text-gray-600">Used {code.times_used} times</p>
                          <p className="text-xs text-gray-400">Created {formatDate(code.created_at)}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyCode(code.code)}
                        className="gap-2"
                      >
                        {copiedCode === code.code ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No referral codes assigned yet</p>
              )}
            </Card>

            {/* Recent Commissions */}
            <Card className="p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Commissions</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('commissions')}
                  className="gap-1 text-purple-600"
                >
                  View All
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              {dashboard?.recent_commissions?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_commissions.slice(0, 5).map((comm) => (
                    <div
                      key={comm.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDate(comm.period_start)} - {formatDate(comm.period_end)}
                        </p>
                        <p className="text-sm text-gray-500">{comm.employee_count} employees</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(comm.commission_amount)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          comm.status === 'paid' 
                            ? 'bg-green-100 text-green-700'
                            : comm.status === 'approved'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {comm.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No commissions yet</p>
              )}
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Referred Companies</h3>
              <p className="text-sm text-gray-500">{companies.length} total</p>
            </div>

            {companies.length > 0 ? (
              <div className="grid gap-4">
                {companies.map((company) => (
                  <Card key={company.id} className="p-5 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">{company.company_name}</h4>
                        {company.location && (
                          <p className="text-sm text-gray-500">{company.location}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Joined {formatDate(company.joined_date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(company.total_commission)}
                        </p>
                        <p className="text-xs text-gray-500">Total earned</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Initial</p>
                        <p className="font-semibold">{company.initial_employees}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Current</p>
                        <p className="font-semibold">{company.current_employees}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Growth</p>
                        <p className={`font-semibold flex items-center justify-center gap-1 ${
                          company.growth > 0 ? 'text-green-600' : company.growth < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {company.growth > 0 ? <ArrowUpRight className="w-4 h-4" /> : 
                           company.growth < 0 ? <ArrowDownRight className="w-4 h-4" /> : 
                           <Minus className="w-4 h-4" />}
                          {Math.abs(company.growth)}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center border border-gray-200">
                <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No referred companies yet</p>
                <p className="text-sm text-gray-400 mt-1">Share your referral code to start earning!</p>
              </Card>
            )}
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Commission History</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="gap-2"
                data-testid="export-commissions-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>

            {commissions.length > 0 ? (
              <Card className="border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Period</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Company</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Employees</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Commission</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((comm) => (
                        <tr key={comm.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            {formatDate(comm.period_start)} - {formatDate(comm.period_end)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{comm.company_name}</td>
                          <td className="px-4 py-3 text-sm text-right">{comm.employee_count}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                            {formatCurrency(comm.commission_amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              comm.status === 'paid' 
                                ? 'bg-green-100 text-green-700'
                                : comm.status === 'approved'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {comm.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center border border-gray-200">
                <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No commissions yet</p>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsTab referrer={referrer} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ referrer }) {
  const [phone, setPhone] = useState(referrer.phone || '');
  const [linkedinUrl, setLinkedinUrl] = useState(referrer.linkedin_url || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await referrerApi.updateProfile({ phone, linkedin_url: linkedinUrl });
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await referrerApi.changePassword({
        password: newPassword,
        confirm_password: confirmPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="grid gap-6 max-w-2xl">
      {/* Profile Settings */}
      <Card className="p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 rounded-lg">
            <User className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Profile Information</h3>
            <p className="text-sm text-gray-500">Update your contact details</p>
          </div>
        </div>

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <Label className="text-gray-700">Full Name</Label>
            <Input
              value={referrer.full_name}
              disabled
              className="mt-1.5 bg-gray-50"
            />
            <p className="text-xs text-gray-400 mt-1">Contact admin to change your name</p>
          </div>

          <div>
            <Label className="text-gray-700">Email</Label>
            <Input
              value={referrer.email}
              disabled
              className="mt-1.5 bg-gray-50"
            />
          </div>

          <div>
            <Label className="text-gray-700">Phone Number</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-gray-700">LinkedIn URL</Label>
            <Input
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://linkedin.com/in/yourprofile"
              className="mt-1.5"
            />
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </Card>

      {/* Password Change */}
      <Card className="p-6 border border-gray-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Change Password</h3>
            <p className="text-sm text-gray-500">Update your portal password</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <Label className="text-gray-700">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              className="mt-1.5"
              required
            />
          </div>

          <div>
            <Label className="text-gray-700">Confirm New Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              className="mt-1.5"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={changingPassword}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </Card>
    </div>
  );
}

// Main Portal Component
export default function ReferrerPortal() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    // Check for existing session
    const token = referrerApi.getToken();
    const savedReferrer = localStorage.getItem('referrer_data');
    
    if (token && savedReferrer) {
      try {
        setReferrer(JSON.parse(savedReferrer));
        setIsLoggedIn(true);
      } catch {
        referrerApi.clearToken();
      }
    }
  }, []);

  const handleLogin = (referrerData) => {
    setReferrer(referrerData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    referrerApi.clearToken();
    setReferrer(null);
    setIsLoggedIn(false);
    toast.success('Logged out successfully');
  };

  if (!isLoggedIn) {
    return <ReferrerLogin onLogin={handleLogin} />;
  }

  return <ReferrerDashboard referrer={referrer} onLogout={handleLogout} />;
}
